import {
    AInesFeatureSet,
    AInesInterestedInJobsiteTypes,
    AInesRichResponseType,
    AInesToolCallType
} from "@/graphql/generated/graphql";
import {getAssistentForFeatureSet, getAvailableAssistants} from "@/helper/chat/assistantFeatureMapping";
import {PromptInfo} from "@/helper/chat/assistantAPI";
import OpenAI from "openai";
import {useChatStore} from "@/helper/chat/chatStore";
import ResponseFormatJSONSchema = OpenAI.ResponseFormatJSONSchema;

/**
 * formated rich response reference
 */
const rr = (richResponse: AInesRichResponseType, params: string = '') : string => `${richResponse}()`


/**
 * formated tool call reference
 */
const tc = (toolCall: AInesToolCallType) : string => `\`${toolCall}\``;

/**
 * formated feature set reference
 */
const fs = (featureSet: AInesFeatureSet) : string => `FeatureSet "${featureSet}"`;

/**
 * formated interested in reference
 */
const ii = (jobsiteType: AInesInterestedInJobsiteTypes) : string => `"${jobsiteType}"`

/**
 * formated interested in reference
 */
const section = (xmlSection: string): string => `Abschnitt \`<${xmlSection}>\``


const indentString = (str:string, count:number = 1, indent = '\t') =>
    str.replace(/^/gm, indent.repeat(count));


const reindentString = (str:string, from:string = '    ', to:string = '\t') =>
    str.replaceAll(from, to);

const RICH_RESPONSE_PROMPT = `Unter dem Attribut \`richResponses\` steht eine Liste von einer beliebigen Anzahl an \`richResponse\` Objekten.
Jedes Objekt folgt dem Format:
\`\`\`
{
    type: $TYPE;
    reference: $REFERENCE;
}
\`\`\`
In der richResponse musst du immer ihren Typen und eine Referenz, die den Datenbereich beschreibt festlegen, über den du gerade konkret sprichst, bzw. bei denen Detailinformationen wichtig sind.

Das JSON Antwortformat sieht also folgendermaßen aus:
\`\`\`
{
    text: string
    richResponses: [
        {
            type: $TYPE;
            reference: $REFERENCE;
        }
    ]
}
\`\`\`
Wobei $TYPE der name des richResponse types sein soll und $REFERENCE die Referenz auf die dazugehörigen Daten und sie sollte das Typenformat der Referenztypen befolgen.
Optionale Angaben werden mit einem ? markiert - sie können auch leer sein.
Listen von einer oder mehreren Angaben werden mit eckigen Klammen (\`[]\`) hinter der Angabe markiert.`

export const getOutputFormatPrompt = (richResponseData:any) => {
    if(!richResponseData){
        return "";
    }
    const result = `<Ausgabeformat>
# Ausgabeformat

Dein Ausgabeformat ist JSON und besteht aus den drei Attributen "text", "cot" und "richResponses":
\`\`\`
{
    text: string,
    cot: string,
    richResponses: richResponse[]
}
\`\`\`

Die ines App zeigt dem Nutzer die Textantwort direkt an und illustriert sie hübsch und übersichtlich mit unterhalb deiner Textantwort grafischen Darstellungen, die wir \`richResponse\` nennen. Wann immer du über etwas sprichst für das es eine passende \`richResponse\` gibt, solltest du auch eine \`richResponse\` anhängen. Sprichst du über mehrere oder verwandte Dinge, kannst du auch mehrere \`richResponse\` Objekte anhängen.
    <AusgabeformatText>
    ## Attribut \`text\`
    Im \`text\` sollst du die reine Textantwort geben und keine Formatierungen wie Markdown verwenden.
    </AusgabeformatText>
    
${indentString(buildRichResponsePrompt(richResponseData))}
</Ausgabeformat>`

    return reindentString(result)
}

const GlobalReferenceTypes = [{
    name:  'HumanRequestType',
    format: "integer",
    description: "Referenziert eine bestimmte Anfrage an das IBFG Team durch ihre id"
}]
const GlobalRichResponseTypes = [
    {
        type: AInesRichResponseType.InitHumanRequest,
        reference: "null",
        description: "Die "+ AInesRichResponseType.InitHumanRequest + " zeigt einen Button an, mit dem der Nutzer eine Anfrage an das IBFG Team auslösen kann."
    },
    {
        type: AInesRichResponseType.HumanRequestResponse,
        reference: "$HumanRequestType",
        description: "Die "+ AInesRichResponseType.HumanRequestResponse + " zeigt eine Zusammenfassung der Anfrage an das IBFG Team an."
    }
]

const buildRichResponsePrompt = (richResponseData:any) => {
    const richResponseDefinitionString = JSON.stringify(richResponseData.richResponseDefinitions.concat(GlobalRichResponseTypes))
    const richResponseReferenceString = JSON.stringify(richResponseData.richResponseReferenceDefinitions.concat(GlobalReferenceTypes))

    return `<AusgabeformatRichResponse>
## Attribut \`richResponses\` 
${RICH_RESPONSE_PROMPT}
### Die möglichen richResponse Typen sind die folgenden:
${richResponseDefinitionString}

### Die möglichen Reference Types sind die folgenden:
${richResponseReferenceString}            
</AusgabeformatRichResponse>

<AusgabeformatChainOfThought>   
## Attribut cot

In dem Attribut \`cot\` (kurz für chain of thought) sollst folgende Frage beantworten: **Soll ich eine oder mehrere \`richResponses\` anhängen? **Wenn du hier zu dem Schluss kommst, dass eine \`richResponse\` angezeit werden sollte, stelle sicher, dass du sie auch tatsächlich als \`richResponse\` in das \`richResponses\` Attribut hinzufügst!
* Für Details an einem Tag verwendest du die Day Calendar richResponse. 
* Für die Übersicht über eine Woche verwendest du die Week Calendar richResponse.
* Für die Übersicht über einen Monat verwendest du die Month Calendar richResponse.
</AusgabeformatChainOfThought>`
}

function getFeatureDescriptionFor(featureSet: AInesFeatureSet): string {
    switch (featureSet) {
        case AInesFeatureSet.Availability:
            return "Der Nutzer verwaltet, an welchen Tagen und in welchen Schichten er bereit ist, zu arbeiten. Es können Verfügbarkeiten gesetzt, gelöscht oder modifiziert werden, jedoch dürfen sie keine bestehenden Dienste überschreiben.";
        case AInesFeatureSet.RegisterBooking:
            return "In diesem Featureset kann der Nutzer bereits persönlich abgesprochene Dienste manuell in den Dienstplan eintragen lassen. Der Assistent fragt die erforderlichen Parameter wie Schicht, Station und Datum ab und unterstützt den Nutzer dabei, diese Dienste zu registrieren.";
        case AInesFeatureSet.PlanningMonth:
            return "Dieses Featureset unterstützt den Nutzer bei der automatisierten Dienstplanung für einzelne Monate. Der Nutzer kann die Planung für einen Monat starten, Ziele für die Anzahl der Dienste festlegen und Stationen für die Dienstplanung aktivieren oder deaktivieren.";
        case AInesFeatureSet.Timesheet:
            return "In diesem Featureset unterstützt der Assistent den Nutzer bei der Erfassung und Signierung von geleisteten Diensten. Hierbei wird die Arbeitszeit minutengenau erfasst und von einer verantwortlichen Person auf der Station digital unterschrieben.";
        case AInesFeatureSet.Sickness:
            return "Der Nutzer kann sich für bestimmte Tage krank melden. Bei bestehenden Diensten muss der Nutzer sich an den Disponenten wenden. Erfolgreich eingetragene Krankheitstage werden angezeigt.";
        case AInesFeatureSet.Vacation:
            return "Der Nutzer kann einzelne Tage als Urlaub markieren. Diese zählen zu den monatlichen Zielstunden der Dienstplanung. Dienste können nicht durch Urlaubsbuchungen überschrieben werden.";
        case AInesFeatureSet.Timeoff:
            return "Der Nutzer kann einzelne Tage als Wunschfrei markieren, um auszudrücken, dass er an diesen Tagen nicht arbeiten möchte.";
        case AInesFeatureSet.Roster:
            return "Das Featureset zeigt dem Nutzer seinen Dienstplan an. Der Assistent holt sich alle eingetragenen Dienste, Verfügbarkeiten und Abwesenheiten (wie z.B. Urlaub oder Wunschfrei) und zeigt dem Nutzer einzelne Tage, Wochen oder einen Monatskalender an.";
        case AInesFeatureSet.JobsitePreferences:
            return "Der Nutzer verwaltet, auf welchen Stationen er bereit ist, zu arbeiten. Dafür kann er seine Lieblingsstationen und seine Alternativstationen verwalten.";
        case AInesFeatureSet.DirectDemand:
            return "Der Nutzer kann von den Stationen direkt angefragt werden. Diese Direktanfragen werden dem Nutzer in der App unter 'Aktuelles' angezeigt und er kann direkt entscheiden, ob er den Dienst machen möchte oder nicht.";
        case AInesFeatureSet.HumanRequest:
            return "Der Nutzer verwaltet seine Anfragen an das IBFG Team.";
        case AInesFeatureSet.EmployeeDemandBrowser:
            return "Das Featureset ermöglicht dem Nutzer die Exploration offener Dienste ihm bekannter und unbekannter Stationen. Er kann damit einen Überblick bekommen, auf welchen Stationen, an welchen Tagen und zu welchen Schichten weitere Arbeitsmöglichkeiten existieren und sich für einzelne Dienste (zusätzlich zu seinen Dienstplanungseinstellungen) verfügbar stellen.";
        case AInesFeatureSet.Kpi:
            return "Alles zur Produktivität, Umsatz und zu Bonuszahlungen des Nutzers.";
        case AInesFeatureSet.BookingWithdrawalBookingSuggestion:
            return "Diensttausch unterstützt den strukturierten Austausch von Diensten zwischen Mitarbeitern. Diensttausch ermöglicht sowohl das Abgeben eigener Dienste als auch das Übernehmen fremder Dienstangebote. Dienste abgeben: Der Nutzer sieht seine eigenen Suchanfragen nach Ersatzmitarbeitern, er kann neue Suchanfragen für eigene Dienste erstellen oder eigene Dienste direkt stornieren und damit los werden. Dienstangebote anderer Mitarbeiter: Der Nutzer kann offene Dienstangebote anderer Mitarbeiter einsehen und passende Dienste direkt übernehmen. Auch Stornierungen werden für eine kurze Zeit noch anderen Mitarbeitern angeboten, bevor sie endgültig storniert werden."
        case AInesFeatureSet.BookingCancel:
            return "Wenn der Nutzer bestehende Dienste aus seinem Dienstplan entfernen möchte, kann er diese stornieren."
        default:
            throw new Error(`missing summary for ${featureSet}`);
    }
}



const getAssistantChooserFeatureDescriptions = async () => {
    return `
<Verfügbarkeiten>
FeatureSet: ${AInesFeatureSet.Availability}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Availability)}

${getFeatureDescriptionFor(AInesFeatureSet.Availability)}

Stichwörter:
"Zeig meine Verfügbarkeiten",
"Welche Verfügbarkeiten habe ich?"
"Ich bin am [Datum] für FD/SD/ND verfügbar."
"Ich kann am [Datum] auch im FD/SD/ND arbeiten."
"Lösche Verfügbarkeit am [Datum]."
"Ändere Verfügbarkeit am [Datum]."
"Schichtzeiten einstellen."
"Meine Frühdienste sollen erst um [Uhrzeit] beginnen."
</Verfügbarkeiten>

<Urlaub>
FeatureSet: ${AInesFeatureSet.Vacation}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Vacation)}

${getFeatureDescriptionFor(AInesFeatureSet.Vacation)}

Stichwörter:
"Zeig mir meine Urlaubstage."
"Trage Urlaub am [Datum] ein."
"Entferne Urlaub am [Datum]."
</Urlaub>

<Krank>
FeatureSet: ${AInesFeatureSet.Sickness}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Sickness)}

${getFeatureDescriptionFor(AInesFeatureSet.Sickness)}

Stichwörter:
"Trage Krankmeldung für den [Datum] ein."
"Entferne Krankmeldung am [Datum]."
</Krank>

<Wunschfrei>
FeatureSet: ${AInesFeatureSet.Timeoff}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Timeoff)}

${getFeatureDescriptionFor(AInesFeatureSet.Timeoff)}

Stichwörter:
"Ich brauche am [Datum] einen freien Tag."
"Kannst du mich am [Datum] freistellen?"
"Ich möchte am [Datum] nicht eingeplant werden."
"Am [Datum] würde ich gerne frei machen."
"Trag mich für den [Datum] als frei ein."
"Kannst du meinen freien Tag vom [Datum] auf den [neues Datum] verschieben?"
"Ich kann am [Datum] doch arbeiten, streiche den freien Tag."
"Nimm mich für den [Datum] wieder in die Planung auf."
"Ich brauche am [Datum] doch keinen freien Tag mehr."
</Wunschfrei>

<Monate_in_Dienstplanung>
FeatureSet: ${AInesFeatureSet.PlanningMonth}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.PlanningMonth)}

${getFeatureDescriptionFor(AInesFeatureSet.PlanningMonth)}

Stichwörter:
Dienstplanung starten:
"Starte die Dienstplanung für [Monat]."
"Plane meine Dienste für [Monat]."
"Ich möchte die Planung für [Monat] beginnen."

"Wie sieht mein [Monat] aus?"
"Wie sieht meine Planung für [Monat] aus?"
"Wie viele Dienste fehlen noch für [Monat]?"
"Wie steht es um meine Dienste im [Monat]?"
"Was ist der Stand meiner Dienstplanung für [Monat]?"
"Wie ist der aktuelle Plan für [Monat]?"
"Wie viele Dienste habe ich im [Monat] schon geplant?"
"Habe ich schon genug Dienste für den [Monat]?"
"Bin ich im [Monat] schon vollständig verplant?"
"Wie weit ist meine Planung für [Monat]?"
"Wie sieht der Dienstplan für [Monat] aus?"
"Wie viele Dienste fehlen noch im [Monat]?"

"Setze mein Ziel auf [Anzahl] Dienste für den [Monat]."
"Ich möchte [Anzahl] Dienste im [Monat] arbeiten."
"Ändere mein Dienstziel für [Monat] auf [Anzahl]."
"Passe die Anzahl meiner Dienste für [Monat] an."

"Füge [Station] zur Dienstplanung für [Monat] hinzu."
"Deaktiviere [Station] für die Planung im [Monat]."
"Welche Stationen sind für die Planung im [Monat] aktiv?"
"Aktiviere meine Lieblingsstationen für den [Monat]."
"Zeige mir, welche Stationen für die Planung in [Monat] aktiviert sind."

"Ändere meine Planung für den [Monat]."
"Ich möchte die Planung für [Monat] anpassen."
</Monate_in_Dienstplanung>

<Dienste_eintragen>
FeatureSet: ${AInesFeatureSet.RegisterBooking}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.RegisterBooking)}

${getFeatureDescriptionFor(AInesFeatureSet.RegisterBooking)}

Stichwörter:
"Trage einen Dienst für den [Datum] ein."
"Ich habe einen Dienst am [Datum], bitte eintragen."
"Ich möchte am [Datum] für Frühdienst/Spätdienst/Nachtdienst auf Station [Station] arbeiten, ist bereits abgesprochen."
"Füge einen Dienst für [Datum] auf [Station] hinzu."
"Kannst du den Dienst am [Datum] eintragen?"
"Ich habe einen Dienst auf [Station] für den [Datum], bitte plane ihn ein."

"Trage Dienste für den [Datum 1], [Datum 2] und [Datum 3] ein."
"Ich habe Dienste an mehreren Tagen, trage sie bitte ein."
"Füge Dienste für diese Woche/den Monat ein."
"Ich möchte für den ganzen Monat Dienste auf [Station] eintragen."
</Dienste_eintragen>

<Zeiterfassung>
FeatureSet: ${AInesFeatureSet.Timesheet}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Timesheet)}

${getFeatureDescriptionFor(AInesFeatureSet.Timesheet)}

Stichwörter:
"Starte die Zeiterfassung."
"Beende die Zeiterfassung."
"Wie viele Stunden habe ich diese Woche gearbeitet?"
"Trag meine Arbeitszeit für [Datum] ein."
"Ich habe am [Datum] von [Startzeit] bis [Endzeit] gearbeitet."
"Zeiten erfassen"
"Dienste abzeichnen"
"Signatur für Dienst"
</Zeiterfassung>

<Dienstplan>
FeatureSet: ${AInesFeatureSet.Roster}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Roster)}

${getFeatureDescriptionFor(AInesFeatureSet.Roster)}

Stichwörter:
"Zeig mir meinen Dienstplan."
"Wie sieht mein Dienstplan für [Woche/Monat] aus?"
"Was steht morgen auf dem Plan?"
"Welche Dienste habe ich am [Datum]?"
</Dienstplan>

<Stationsverwaltung>
FeatureSet: ${AInesFeatureSet.JobsitePreferences}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.JobsitePreferences)}

${getFeatureDescriptionFor(AInesFeatureSet.JobsitePreferences)}

Stichwörter:
"Füge [Station] zu meinen Favoriten hinzu."
"Entferne [Station] aus meinen Lieblingsstationen."
"Welche Stationen habe ich als Favoriten markiert?"
"Zeige mir meine Lieblingsstationen."
"Ich möchte nur auf bestimmten Stationen arbeiten."
</Stationsverwaltung>

<Dienststöbern>
FeatureSet: ${AInesFeatureSet.EmployeeDemandBrowser}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.EmployeeDemandBrowser)}

${getFeatureDescriptionFor(AInesFeatureSet.EmployeeDemandBrowser)}

Stichwörter:
"Welche Dienste sind nächste Woche frei?"
"Zeig mir offene Dienste für [Datum/Zeitraum]."
"Gibt es Dienste auf [Station]?"
"Ich suche Spätdienste im [Monat]."
"Zeig mir passende Dienste für mich."
"Wieviel Bedarf/Anfragen/offene Dienste gibt es auf [Station]?"
"Wieviel Bedarf/Anfragen/offene Dienste gab es im letzten halben Jahr es auf [Station]?"
"Was gibt es für [Stationstyp/Fachbereich/Krankenhaus] im [Himmelsrichtung/Bezirk] von [Stadt]?"
"Schlag mir eine neue Station vor"
</Dienststöbern>


<Direktanfragen>
FeatureSet: ${AInesFeatureSet.DirectDemand}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.DirectDemand)}

${getFeatureDescriptionFor(AInesFeatureSet.DirectDemand)}

Stichwörter:
"Habe ich neue Direktanfragen?"
"Zeig mir alle Direktanfragen."
"Ich möchte Direktanfragen für [Zeitraum] sehen."
"Antworte auf Direktanfrage für [Datum]: Zusagen/Absagen."
</Direktanfragen>

<Anfragen_an_das_IBFG_Team>
FeatureSet: ${AInesFeatureSet.HumanRequest}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.HumanRequest)}

${getFeatureDescriptionFor(AInesFeatureSet.HumanRequest)}

Stichwörter:
"Ich habe eine Frage an das Team."
"Kann mich jemand vom Team kontaktieren?"
"Ich brauche Unterstützung bei meiner Planung."
"Bitte leite das an das IBFG-Team weiter."
"Kannst du meine Dienste in meinen Kalender übertragen?"
"Wie kann ich meinen Dienstplan mit meinem privaten Kalender synchronisieren?"
</Anfragen_an_das_IBFG_Team>
<Monatsauswertung>
FeatureSet: ${AInesFeatureSet.Kpi}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.Kpi)}

${getFeatureDescriptionFor(AInesFeatureSet.Kpi)}

Stichwörter:
"Was war letzten Monat mein Umsatz?"
"Wieviele Leute habe ich aktuell Rekrutiert?"
"Wie läuft es gerade für die IBFG?"
"Was ist aktuell der Gewinn der IBFG und wieviel davon bekomme ich?"
"Wie berechnet sich mein Anteil an den IBFG Gewinnen?"
"Wie hoch ist mein Überschuss?"
"Zeig mir meine Monatsabrechnung!"
"Zeig mir die Vereinszahlen!"
"Wie sind die aktuellen Kennzahlen?"
"Kann ich die Monatsauswertung sehen?"
"Wieviel Geld habe ich im $month bekommen?"
"Was weißt du über meine Gehaltsabrechnung?"

</Monatsauswertung>
<Diensttausch>
FeatureSet: ${AInesFeatureSet.BookingWithdrawalBookingSuggestion}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.BookingWithdrawalBookingSuggestion)}

${getFeatureDescriptionFor(AInesFeatureSet.BookingWithdrawalBookingSuggestion)}

Stichwörter:
(Dienst gebende Seite)
"Ich möchte den Dienst am [Datum] nicht machen. Können wir nach Ersatz suchen?"
"Gibt es jemanden, der meinen Dienst am [Datum] übernehmen könnte?"
"Wie kann ich meine Dienste wieder abgeben?"

"Wie steht es um meine Diensttauschanfragen?"
"Wie ist der Status meiner Ersatzsuchen?"
"Hast du schon Ersatz für den Dienst am [Datum] gefunden?"
"Hat jemand meinen Dienst am [Datum] übernommen?"

(Dienst nehmende Seite)
"Gibt es gerade passende Diensttauschanfragen?"
"Kann ich gerade einem Kollegen aus der Patsche helfen?"

</Diensttausch>
<Stornieren>
FeatureSet: ${AInesFeatureSet.BookingCancel}
Expertenassitent: ${await getAssistentForFeatureSet(AInesFeatureSet.BookingCancel)}

${getFeatureDescriptionFor(AInesFeatureSet.BookingCancel)}

Stichwörter:
"Bitte storniere den Dienst am [Datum]"
"Ich möchte den Dienst am [Datum] nicht machen. Können wie nach Ersatz suchen?"
"Wie kann ich Dienste stornieren?"
"Ich wurde umgebucht, kann ich stornieren?"
"Die Schicht am [Datum] wurde geändert, und ich kann da nicht!"
"Der [Entleiher] hat die Station / das Haus geändert - das ist viel zu weit weg."
</Stornieren>
`
}

export const  getMetaPromptInfo = async (): Promise<PromptInfo>  => {
    const listOfFeatures = await getAssistantChooserFeatureDescriptions();

    const prompt = `
<FeatureSets> 
${indentString(listOfFeatures)}
</FeatureSets>

<Aufgabe>
    # Aufgabe
    Du bist ein Experte für eine Menge an FeatureSets und die Expertenassistenten, die für diese FeatureSets jeweils verantwortlich sind. Für jedes FeatureSet, das es gibt, hast du eben im ${section('FeatureSets')} eine Beschreibung bekommen und die Information, welcher Expertenassistent dazu gehört. 
    
    Deine Aufgabe ist es, die Beschreibung und die Stichwörter zu analysieren und dann zu entscheiden, welches der vielen FeatureSets am ehesten zu der Nutzeranfrage passt und den entsprechenden Expertenassistenten auszuwählen. Die Nutzeranfrage wird dann an den von dir gewählten Expertenassistenten weitergeleitet und dort beantwortet.
      
    Verbleib bei einem Experten: Im Falle einer Ambivalenz kann angenommen werden, dass die Nutzeranfrage sich auf denselben Expertenassistenten bezieht, der zuvor aktiv war. 
      
    Überblick: Wenn der Nutzer keine konkrete Frage zu einem FeatureSet stellt, sondern eher einen Überblick über die Funktionsweise der App benötigt, verwende das ${fs(AInesFeatureSet.FeatureOverview)}.
    
    Anfrage an das IBFG Team: Nur für den Fall, dass der Nutzer etwas fragt, das 
    1. zu keinem Feature und auch nicht zum ${fs(AInesFeatureSet.FeatureOverview)} passt und 
    2. das in den Themenbereich Zeitarbeit bzw. Pflege fällt oder mit der Arbeit mit der ines App oder IBFG zu tun hat, und 
    3. bei dem ein Mitarbeiter der IBFG bzw. ein Disponent helfen könnte, 
    verwende ${fs(AInesFeatureSet.HumanRequest)}. 
</Aufgabe>
    `

    const response_format : ResponseFormatJSONSchema = {
        type: "json_schema",
        json_schema: {
            name: "chooser_response",
            strict: true,
            schema: {
                type: "object",
                properties: {
                    cot: {
                        type: "string",
                        description: "Analyse der Anfrage des Nutzers und Begründung, welches FeatureSet und damit welcher Expertenassistent gewählt werden soll."
                    },
                    chosenExpertAssistant: {
                        type: "string",
                        enum: (await getAvailableAssistants()),
                        description: "Der Expertenassistent für das Feature, das am besten zur Anfrage des Nutzers passt."
                    }
                },
                additionalProperties: false,
                required: [
                    "cot",
                    "chosenExpertAssistant"
                ]
            }
        }
    }

    return {
        model: import.meta.env.VITE_CHOOSER_MODEL,
        prompt,
        response_format,
        tools: []
    }
}

export const generateMetaPromptMessageThread = async (userMessage: string): Promise<OpenAI.ChatCompletionMessageParam[]>  => {
    let messages = useChatStore().chatMessageHistory.slice()

    // ensure the user message is part of the thread - and prevent it from appearing twice
    const mostRecentMessage = messages.length > 0 ? messages[messages.length - 1] : undefined
    if (!mostRecentMessage || mostRecentMessage.role !== 'user' || mostRecentMessage.text !== userMessage) {
        messages.push({
            role:'user',
            text: userMessage
        })
    }

    const chatHistoryMessages = messages.map((m) => {
        let content = ''
        switch (m.role) {
            case 'assistant':
                content = `Gewählter Expertenassistent:\n${m.expert}\nExpertenantwort:\n${m.text}`
                break
            case 'user':
                content = `Nutzeranfrage:\n${m.text}`
                break
        }

        return {
            role: m.role,
            content
        }
    })

    // consoleLogChat("chatMessageHistory for chooser:\n", chatHistoryMessages.map(m => `${m.role}: ${m.content}`).join('\n'))

    return chatHistoryMessages
}
